import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useDemoStore } from '@/stores/demo'
import i18n from '@/i18n'
import '@/types/router' // Import router type extensions

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public landing page
    {
      path: '/',
      name: 'landing',
      component: () => import('../views/landing/LandingView.vue'),
      meta: { public: true, layout: 'landing', titleKey: 'pageTitles.landing' },
    },
    // Live demo bootstrap: opened in a new tab from the landing CTA. Claims a
    // memory-only demo session (so it never clobbers a real session in another
    // tab via shared same-origin storage) then redirects into the real app.
    {
      path: '/demo',
      name: 'demo',
      component: () => import('../views/DemoBootView.vue'),
      meta: { public: true, titleKey: 'pageTitles.landing' },
    },
    // Embedded live demo (grafted from the origin line): iframe-friendly demo
    // shell that redirects into the showcased modules under DemoEmbedLayout.
    {
      path: '/demo-embed',
      name: 'demo-embed',
      component: () => import('../views/DemoEmbedView.vue'),
      meta: { public: true, layout: 'demo-embed', titleKey: 'pageTitles.landing' },
    },
    // Dashboard (protected)
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../views/DashboardView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.dashboard' },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/auth/LoginView.vue'),
      meta: { requiresGuest: true, titleKey: 'pageTitles.login' },
    },
    {
      path: '/set-password/:token',
      name: 'set-password',
      component: () => import('../views/auth/SetPasswordView.vue'),
      meta: { requiresGuest: true, titleKey: 'pageTitles.setPassword' },
    },
    {
      path: '/customers',
      name: 'customers',
      component: () => import('../views/customers/CustomerListView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.customers' },
    },
    {
      path: '/customers/new',
      name: 'customer-create',
      component: () => import('../views/customers/CustomerCreateView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.newCustomer' },
    },
    {
      path: '/customers/:id',
      name: 'customer-profile',
      component: () => import('../views/customers/CustomerProfileView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.customerProfile' },
    },
    {
      path: '/customers/:id/edit',
      name: 'customer-edit',
      component: () => import('../views/customers/CustomerEditView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.editCustomer' },
    },
    {
      path: '/collaborators',
      name: 'collaborators',
      component: () => import('../views/collaborators/CollaboratorListView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales', 'accountant'],
        titleKey: 'pageTitles.collaborators',
      },
    },
    {
      path: '/collaborators/new',
      name: 'collaborator-create',
      component: () => import('../views/collaborators/CollaboratorCreateView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales'],
        titleKey: 'pageTitles.newCollaborator',
      },
    },
    {
      path: '/collaborators/:id',
      name: 'collaborator-profile',
      component: () => import('../views/collaborators/CollaboratorProfileView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales', 'accountant'],
        titleKey: 'pageTitles.collaboratorProfile',
      },
    },
    {
      path: '/collaborators/:id/edit',
      name: 'collaborator-edit',
      component: () => import('../views/collaborators/CollaboratorEditView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales'],
        titleKey: 'pageTitles.editCollaborator',
      },
    },
    // Sales unified into Orders (a sale is a line-item inside an order).
    // These redirects keep any stale links/bookmarks working.
    { path: '/sales', redirect: '/orders' },
    { path: '/sales/dashboard', redirect: '/orders/board' },
    { path: '/sales/new', redirect: '/orders/quick' },
    { path: '/sales/:id', redirect: '/orders' },
    {
      path: '/deliveries',
      name: 'deliveries',
      component: () => import('../views/deliveries/DeliveryListView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.deliveries' },
    },
    {
      path: '/deliveries/kanban',
      name: 'delivery-kanban',
      component: () => import('../views/deliveries/DeliveryKanbanView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.kanban' },
    },
    {
      path: '/deliveries/assign',
      name: 'delivery-assignment',
      component: () => import('../views/deliveries/DeliveryAssignmentView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales'],
        titleKey: 'pageTitles.assignDelivery',
      },
    },
    {
      path: '/deliveries/provider-fees',
      name: 'provider-fees',
      component: () => import('../views/deliveries/ProviderFeesView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'accountant'],
        titleKey: 'pageTitles.providerFees',
      },
    },
    {
      path: '/deliveries/performance',
      name: 'deliverer-performance',
      component: () => import('../views/deliveries/DelivererPerformanceView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales'],
        titleKey: 'pageTitles.delivererPerformance',
      },
    },
    {
      path: '/deliveries/:id',
      name: 'delivery-detail',
      component: () => import('../views/deliveries/DeliveryDetailView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.deliveryDetail' },
    },
    {
      path: '/deliveries/:id/reassign',
      name: 'delivery-reassign',
      component: () => import('../views/deliveries/DeliveryReassignView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.reassignDelivery' },
    },
    {
      path: '/delivery-zones',
      name: 'delivery-zones',
      component: () => import('../views/deliveries/DeliveryZonesListView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.deliveryZones' },
    },
    {
      path: '/products',
      name: 'products',
      component: () => import('../views/products/ProductListView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.products' },
    },
    {
      path: '/products/new',
      name: 'product-create',
      component: () => import('../views/products/ProductFormView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.newProduct' },
    },
    {
      path: '/products/:id',
      name: 'product-detail',
      component: () => import('../views/products/ProductDetailView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.productDetail' },
    },
    {
      path: '/products/:id/edit',
      name: 'product-edit',
      component: () => import('../views/products/ProductFormView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.editProduct' },
    },
    {
      path: '/product-items',
      name: 'product-items',
      component: () => import('../views/products/ProductItemsListView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.productItems' },
    },
    {
      // Canonical "add stock" — single mode (optional ?productId for context).
      path: '/product-items/new',
      name: 'product-items-create',
      component: () => import('../views/products/ProductItemsCreateView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.addStock' },
    },
    {
      // Canonical "add stock" — bulk / multi-product mode.
      path: '/product-items/bulk',
      name: 'product-items-bulk-create',
      component: () => import('../views/products/ProductItemsBulkCreateView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.bulkAddStock' },
    },
    // Redirects from the three legacy add-stock paths to the canonical routes.
    {
      path: '/products/:productId/items/new',
      redirect: (to) => ({
        path: '/product-items/new',
        query: { productId: String(to.params.productId) },
      }),
    },
    { path: '/product-items/create', redirect: '/product-items/new' },
    { path: '/product_items/new', redirect: '/product-items/bulk' },
    {
      path: '/products/import',
      name: 'product-import',
      component: () => import('../views/products/ProductImportView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales'],
        titleKey: 'pageTitles.importProducts',
      },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('../views/settings/SettingsView.vue'),
      meta: { requiresAuth: true, roles: ['admin'], titleKey: 'pageTitles.settings' },
    },
    // Live shopping (feature-gated). Router guard checks
    // user.organization.features.live_shopping; backend independently 403s.
    {
      path: '/live-sessions',
      name: 'live-sessions',
      component: () => import('../views/live_sessions/LiveSessionListView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales', 'accountant'],
        feature: 'live_shopping',
        titleKey: 'pageTitles.liveSessions',
      },
    },
    {
      path: '/live-sessions/new',
      name: 'live-session-new',
      component: () => import('../views/live_sessions/LiveSessionFormView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales'],
        feature: 'live_shopping',
        titleKey: 'pageTitles.newLiveSession',
      },
    },
    {
      path: '/live-sessions/:id',
      name: 'live-session-detail',
      component: () => import('../views/live_sessions/LiveSessionDetailView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales', 'accountant'],
        feature: 'live_shopping',
        titleKey: 'pageTitles.liveSessionDetail',
      },
    },
    {
      path: '/live-sessions/:id/capture',
      name: 'live-session-capture',
      component: () => import('../views/live_sessions/LiveSessionCaptureView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales'],
        feature: 'live_shopping',
        titleKey: 'pageTitles.liveCapture',
      },
    },
    {
      path: '/shipments',
      name: 'shipments',
      component: () => import('../views/shipments/ShipmentListView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.shipments' },
    },
    {
      path: '/shipments/new',
      name: 'shipment-create',
      component: () => import('../views/shipments/ShipmentFormView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.newShipment' },
    },
    {
      path: '/shipments/:id',
      name: 'shipment-detail',
      component: () => import('../views/shipments/ShipmentDetailView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.shipmentDetail' },
    },
    {
      path: '/shipments/:id/edit',
      name: 'shipment-edit',
      component: () => import('../views/shipments/ShipmentFormView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.editShipment' },
    },
    {
      path: '/shippers',
      name: 'shippers',
      component: () => import('../views/shippers/ShipperListView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.shippers' },
    },
    {
      path: '/shippers/new',
      name: 'shipper-create',
      component: () => import('../views/shippers/ShipperFormView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.newShipper' },
    },
    {
      path: '/shippers/:id',
      name: 'shipper-detail',
      component: () => import('../views/shippers/ShipperDetailView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.shipperDetail' },
    },
    {
      path: '/shippers/:id/edit',
      name: 'shipper-edit',
      component: () => import('../views/shippers/ShipperFormView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.editShipper' },
    },
    {
      path: '/orders',
      name: 'orders',
      component: () => import('../views/orders/OrderListView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.orders' },
    },
    {
      path: '/orders/new',
      name: 'order-create',
      component: () => import('../views/orders/OrderWizardView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.newOrder' },
    },
    {
      path: '/orders/board',
      name: 'orders-board',
      component: () => import('../views/orders/OrdersBoardView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.ordersBoard' },
    },
    {
      // Quick order = fast single-line entry (reuses NewSaleView; server creates the order)
      path: '/orders/quick',
      name: 'order-quick',
      component: () => import('../views/sales/NewSaleView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.quickOrder' },
    },
    {
      path: '/orders/:id',
      name: 'order-detail',
      component: () => import('../views/orders/OrderDetailView.vue'),
      meta: { requiresAuth: true, titleKey: 'pageTitles.orderDetail' },
    },
    {
      path: '/orders/:id/edit',
      name: 'order-edit',
      component: () => import('../views/orders/OrderFormView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.editOrder' },
    },
    // Payments routes
    {
      path: '/payments',
      name: 'payments',
      component: () => import('../views/payments/PaymentListView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales', 'accountant'],
        titleKey: 'pageTitles.payments',
      },
    },
    {
      path: '/payments/:id',
      name: 'payment-detail',
      component: () => import('../views/payments/PaymentDetailView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales', 'accountant'],
        titleKey: 'pageTitles.paymentDetail',
      },
    },
    // Arrivals routes
    {
      path: '/arrivals',
      name: 'arrivals',
      component: () => import('../views/arrivals/ArrivalListView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales', 'accountant'],
        titleKey: 'pageTitles.arrivals',
      },
    },
    {
      path: '/arrivals/new',
      name: 'arrival-create',
      component: () => import('../views/arrivals/ArrivalWizardView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.newArrival' },
    },
    {
      path: '/arrivals/:id',
      name: 'arrival-detail',
      component: () => import('../views/arrivals/ArrivalDetailView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'sales', 'accountant'],
        titleKey: 'pageTitles.arrivalDetail',
      },
    },
    {
      path: '/arrivals/:id/edit',
      name: 'arrival-edit',
      component: () => import('../views/arrivals/ArrivalFormView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'sales'], titleKey: 'pageTitles.editArrival' },
    },
    // Settlements routes
    {
      path: '/settlements',
      name: 'settlements',
      component: () => import('../views/settlements/SettlementListView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'accountant'],
        titleKey: 'pageTitles.settlements',
      },
    },
    {
      path: '/settlements/new',
      name: 'settlement-create',
      component: () => import('../views/settlements/SettlementWizardView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'accountant'],
        titleKey: 'pageTitles.newSettlement',
      },
    },
    {
      path: '/settlements/:id',
      name: 'settlement-detail',
      component: () => import('../views/settlements/SettlementDetailView.vue'),
      meta: {
        requiresAuth: true,
        roles: ['admin', 'accountant'],
        titleKey: 'pageTitles.settlementDetail',
      },
    },
    // 404 - catch all route (must be last)
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue'),
      meta: { titleKey: 'pageTitles.notFound' },
    },
  ],
})

// Screens that reveal the proprietary zone -> shipper-office "bucket" routing.
// Hard-blocked in the live demo (defense-in-depth on top of the filtered demo
// dock + the single-city/cross-city-disabled demo org).
const DEMO_BLOCKED_PREFIXES = ['/shipments', '/shippers', '/delivery-zones']

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const demoStore = useDemoStore()

  // Re-arm a per-tab demo session before any other check, so a reload / hard
  // navigation can't slip past containment in the window before App mounts.
  if (!demoStore.active) demoStore.restore()

  // Demo containment: keep the demo session inside its safe surface.
  if (demoStore.active && DEMO_BLOCKED_PREFIXES.some((p) => to.path.startsWith(p))) {
    return next('/deliveries/kanban')
  }

  // Redirect authenticated users from landing page to dashboard
  if (to.path === '/' && to.meta.public && authStore.isAuthenticated) {
    return next('/dashboard')
  }

  // Public routes are always accessible
  if (to.meta.public) {
    return next()
  }

  // If we have an active session but no token, attempt to refresh first
  // This handles page refresh where token is memory-only but session cookie exists
  const needsSessionRecovery = !authStore.isAuthenticated && authStore.hasActiveSession

  if (needsSessionRecovery) {
    try {
      // Attempt to restore session via silent refresh
      const success = await authStore.refreshTokenIfNeeded()

      if (success && authStore.isAuthenticated) {
        // Session recovered - now check where to go
        if (to.meta.requiresGuest) {
          // User is now authenticated, redirect away from guest pages
          return next('/dashboard')
        }

        // Check role-based permissions before continuing
        if (to.meta.roles && Array.isArray(to.meta.roles)) {
          const userRole = authStore.user?.role

          if (!userRole || !to.meta.roles.includes(userRole)) {
            // User doesn't have required role - redirect to dashboard with error
            if (import.meta.env.DEV) {
              console.warn(
                `Access denied: User role "${userRole}" not in allowed roles [${to.meta.roles.join(', ')}] for route ${to.path}`,
              )
            }

            return next({
              path: '/dashboard',
              query: {
                error: 'insufficient_permissions',
                attempted: to.path,
              },
            })
          }
        }

        // Feature-flag check (post-session-recovery branch).
        if (to.meta.feature) {
          const features = (authStore.user as any)?.organization?.features ?? {}
          const featureKey = to.meta.feature as string
          const val = features[featureKey]
          const enabled =
            val === true ||
            (val && typeof val === 'object' && (val as { enabled?: boolean }).enabled === true)
          if (!enabled) {
            if (import.meta.env.DEV) {
              console.warn(
                `Feature "${featureKey}" not enabled for this organization; route ${to.path}`,
              )
            }
            return next({
              path: '/dashboard',
              query: { error: 'feature_disabled', attempted: to.path },
            })
          }
        }

        // Continue to the requested page
        return next()
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Session recovery failed:', error)
      }
    }

    // Refresh failed - handle based on route requirements
    if (to.meta.requiresAuth) {
      return next('/login')
    }
    // If going to guest page and refresh failed, that's fine - continue
  }

  // Standard auth checks (for when session is already established)
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return next('/login')
  }

  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    return next('/dashboard')
  }

  // Role-based authorization check
  if (to.meta.roles && Array.isArray(to.meta.roles) && authStore.isAuthenticated) {
    const userRole = authStore.user?.role

    if (!userRole || !to.meta.roles.includes(userRole)) {
      // User doesn't have required role - redirect to dashboard with error
      if (import.meta.env.DEV) {
        console.warn(
          `Access denied: User role "${userRole}" not in allowed roles [${to.meta.roles.join(', ')}] for route ${to.path}`,
        )
      }

      return next({
        path: '/dashboard',
        query: {
          error: 'insufficient_permissions',
          attempted: to.path,
        },
      })
    }
  }

  // Org-feature-flag check (audience gating). Backend independently 403s
  // on the same flag; this is just frontend hygiene to avoid 403 toasts.
  if (to.meta.feature && authStore.isAuthenticated) {
    const features = (authStore.user as any)?.organization?.features ?? {}
    const featureKey = to.meta.feature as string
    const val = features[featureKey]
    const enabled =
      val === true ||
      (val && typeof val === 'object' && (val as { enabled?: boolean }).enabled === true)

    if (!enabled) {
      if (import.meta.env.DEV) {
        console.warn(`Feature "${featureKey}" not enabled for this organization; route ${to.path}`)
      }
      return next({
        path: '/dashboard',
        query: { error: 'feature_disabled', attempted: to.path },
      })
    }
  }

  next()
})

// Dynamic page title - updates browser tab after each navigation
router.afterEach((to) => {
  const baseTitle = 'HudStore'
  const titleKey = to.meta.titleKey

  if (titleKey) {
    // Use i18n to translate the title key
    const pageTitle = i18n.global.t(titleKey as string)
    document.title = `${pageTitle} | ${baseTitle}`
  } else {
    document.title = baseTitle
  }
})

export default router
